import React, { useEffect, useState } from "react";
import {
  Alert,
  Card,
  Collapse,
  Descriptions,
  Divider,
  Input,
  message,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import dayjs from "dayjs";
import { formatPrice } from "../../../../../functions/formatters";
import { CURRENCY } from "../../../../../constants/DeliveryConstants";
import { FIRESTORE_ORDERS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { addDoc, collection, updateDoc } from "firebase/firestore";
import ProcessOrdersProgressModal from "./ProcessOrdersProgressModal";
import axios from "axios";
import urls from "../../../../../urls";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { JLF_PICKED_UP } from "../../../../../constants/OrderConstant";

const { Title, Text } = Typography;

// Helper function for generating order references with defaultReference fallback
const generateOrderReferences = (orders, customers) => {
  return (orders || []).map((order) => {
    if (order.reference) return order.reference;
    // Find customer and use defaultReference as fallback
    const customer = customers?.find((c) => c.id === order.farmorderCustomerId);
    return customer?.defaultReference || "";
  });
};

const PreviewExcelOrdersModal = ({ visible, onCancel, onSuccess, orders = [], skippedOrders = [], customers = [] }) => {
  const { t } = useTranslation();
  const { email } = useAuth();
  const [loading, setLoading] = useState(false);

  // Progress modal states
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [currentOrder, setCurrentOrder] = useState(null);
  const [currentOperation, setCurrentOperation] = useState(null);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [isProcessingComplete, setIsProcessingComplete] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);

  const [references, setReferences] = useState(() => generateOrderReferences(orders, customers));
  const [shippingToggles, setShippingToggles] = useState(() =>
    orders.map((order) => parseFloat(order.shippingCost || 0) > 0 && !order.shippingCostDisabled),
  );

  // Keep defaults in sync when opening or when orders change
  useEffect(() => {
    const next = generateOrderReferences(orders, customers);
    setReferences(next);
    setShippingToggles(orders.map((order) => parseFloat(order.shippingCost || 0) > 0 && !order.shippingCostDisabled));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, orders, customers]);

  const processOrders = async () => {
    try {
      setLoading(true);

      // Validate all orders have delivery dates before processing
      const ordersWithoutDeliveryDate = orders.filter(
        (order) => !order.farmorderDeliveryDate || order.farmorderDeliveryDate.trim() === "",
      );

      if (ordersWithoutDeliveryDate.length > 0) {
        message.error(t("notifications.orders_missing_delivery_date"));
        setLoading(false);
        return;
      }

      // EDIFACT-orders met onopgeloste onbekende producten (GTIN zonder match)
      // mogen niet bevestigd worden — anders zou een besteld artikel stilzwijgend
      // wegvallen uit de order.
      const ordersWithUnknownProducts = orders.filter((order) => order.edifactUnknownLines?.length > 0);
      if (ordersWithUnknownProducts.length > 0) {
        message.error(t("notifications.orders_have_unknown_products"));
        setLoading(false);
        return;
      }

      // Close preview modal and open progress modal
      onCancel();
      setShowProgressModal(true);
      setProgress({ current: 0, total: orders.length });
      setCompletedOrders([]);
      setIsProcessingComplete(false);
      setCurrentOrder(null);
      setCurrentOperation(null);

      const results = {
        successful: [],
        partial: [],
        failed: [],
        total: orders.length,
      };

      // Process orders sequentially
      for (let i = 0; i < orders.length; i++) {
        const order = orders[i];
        const reference = references?.[i] || "";
        const addShippingCost = shippingToggles[i];

        // Update current order being processed
        setCurrentOrder(order);
        setProgress({ current: i, total: orders.length });

        // Track operations for this order
        const orderOperations = {
          firestore: { attempted: false, success: false, error: null, response: null },
          processOrder: { attempted: false, success: false, error: null, response: null },
          stock: { attempted: false, success: false, error: null, response: null },
        };

        let farmorderId = null;

        try {
          // 1. CREATE ORDER IN FIRESTORE
          setCurrentOperation("firestore");
          orderOperations.firestore.attempted = true;

          // Recalculate total amount based on shipping toggle
          const shippingCost = addShippingCost ? parseFloat(order.shippingCost) : 0;
          const subTotalAmount = order.subTotalAmount;
          const deliveryTimeCharge = order.deliveryTimeCharge || 0;
          const totalAmount = subTotalAmount + shippingCost + deliveryTimeCharge;

          const orderObj = {
            ...order,
            farmorderId: null,
            reference: reference,
            shippingCost: shippingCost.toString(),
            shippingCostDisabled: !addShippingCost,
            totalAmount: totalAmount,
          };

          // Validate order object for undefined fields before sending to Firestore
          const undefinedFields = [];

          Object.entries(orderObj).forEach(([key, value]) => {
            if (value === undefined) {
              undefinedFields.push({ field: key, value: null });
            } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
              // Check nested objects
              Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                if (nestedValue === undefined) {
                  undefinedFields.push({ field: `${key}.${nestedKey}`, value: null });
                }
              });
            } else if (Array.isArray(value)) {
              // Check arrays (especially userShoppingCart)
              value.forEach((item, idx) => {
                if (item === undefined) {
                  undefinedFields.push({ field: `${key}[${idx}]`, product: null });
                } else if (typeof item === "object" && item !== null) {
                  Object.entries(item).forEach(([itemKey, itemValue]) => {
                    if (itemValue === undefined) {
                      // Special handling for shopping cart items to show product details
                      if (key === "userShoppingCart") {
                        const productInfo = {
                          index: idx + 1,
                          productName: item.productName || "Unknown Product",
                          productId: item.productId || "No ID",
                          field: itemKey,
                        };
                        undefinedFields.push(productInfo);
                      } else {
                        undefinedFields.push({ field: `${key}[${idx}].${itemKey}`, value: item });
                      }
                    }
                  });
                }
              });
            }
          });

          // If undefined fields found, throw error with detailed information
          if (undefinedFields.length > 0) {
            const productErrors = undefinedFields.filter((f) => f.productName);
            const otherErrors = undefinedFields.filter((f) => !f.productName);

            const errorParts = [];

            // Add product-specific errors
            if (productErrors.length > 0) {
              const productDetails = productErrors
                .map((p) => `${p.productName} (${t("missing")}: ${p.field})`)
                .join(", ");
              errorParts.push(`${t("invalid_product_data")}: ${productDetails}`);
            }

            // Add other field errors
            if (otherErrors.length > 0) {
              const fieldNames = otherErrors.map((f) => f.field).join(", ");
              errorParts.push(`${t("missing_required_fields")}: ${fieldNames}`);
            }

            throw new Error(errorParts.join(" | "));
          }

          const docRef = await addDoc(
            collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE),
            orderObj,
          );

          farmorderId = docRef.id;
          await updateDoc(docRef, { farmorderId: farmorderId, updatedAt: dayjs().unix(), updatedBy: email });

          orderOperations.firestore.success = true;

          // 2. CALL APIS IN PARALLEL
          const processToken = await getFirebaseIDToken();

          const apiPromises = [];
          let processOrderResponseData = null;
          let stockResponseData = null;

          // Ensure API receives the Firestore document id at farmorder.id
          const orderPayload = {
            ...orderObj,
            farmorderId,
            id: farmorderId,
          };

          // Process Order API
          setCurrentOperation("processOrder");
          orderOperations.processOrder.attempted = true;
          apiPromises.push(
            axios
              .post(urls.processOrder, orderPayload, { headers: { Authorization: processToken } })
              .then((res) => {
                processOrderResponseData = res?.data || null;
                orderOperations.processOrder.response = processOrderResponseData;
                // Success is true only if API indicates success
                if (processOrderResponseData && typeof processOrderResponseData.success === "boolean") {
                  orderOperations.processOrder.success = !!processOrderResponseData.success;
                  if (!processOrderResponseData.success) {
                    orderOperations.processOrder.error =
                      processOrderResponseData.message || t("process_order_returned_an_error");
                  }
                } else {
                  // Fallback: if no explicit success key, assume ok
                  orderOperations.processOrder.success = true;
                }
              })
              .catch((error) => {
                orderOperations.processOrder.error = error.response?.data?.message || error.message;
                orderOperations.processOrder.response = error.response?.data || null;
              }),
          );

          // Stock API
          setCurrentOperation("stock");
          orderOperations.stock.attempted = true;
          apiPromises.push(
            axios
              .post(urls.stockApiUrl, orderPayload, { headers: { Authorization: processToken } })
              .then((res) => {
                stockResponseData = res?.data || null;
                orderOperations.stock.response = stockResponseData;
                if (stockResponseData && typeof stockResponseData.success === "boolean") {
                  orderOperations.stock.success = !!stockResponseData.success;
                  if (!stockResponseData.success) {
                    orderOperations.stock.error = stockResponseData.message || t("stock_api_returned_an_error");
                  }
                } else {
                  orderOperations.stock.success = true;
                }
              })
              .catch((error) => {
                orderOperations.stock.error = error.response?.data?.message || error.message;
                orderOperations.stock.response = error.response?.data || null;
              }),
          );

          // Wait for both API calls to complete
          await Promise.all(apiPromises);

          // Determine overall status
          const allSuccess =
            orderOperations.firestore.success && orderOperations.processOrder.success && orderOperations.stock.success;

          const anyApiFailure = !orderOperations.processOrder.success || !orderOperations.stock.success;

          if (allSuccess) {
            // Complete success
            results.successful.push({ order, farmorderId, operations: orderOperations });
            setCompletedOrders((prev) => [
              ...prev,
              {
                order,
                status: "success",
                farmorderId,
                operations: { ...orderOperations },
              },
            ]);
          } else if (orderOperations.firestore.success && anyApiFailure) {
            // Firestore succeeded but APIs failed - need cleanup
            results.partial.push({ order, farmorderId, operations: { ...orderOperations }, needsCleanup: false });
            setCompletedOrders((prev) => [
              ...prev,
              {
                order,
                status: "partial",
                farmorderId,
                operations: { ...orderOperations },
              },
            ]);
          } else {
            // Complete failure
            results.failed.push({ order, farmorderId, operations: orderOperations });
            setCompletedOrders((prev) => [
              ...prev,
              {
                order,
                status: "error",
                farmorderId,
                operations: orderOperations,
              },
            ]);
          }
        } catch (error) {
          console.error(`Error processing order for ${order.farmorderCustomerName}:`, error);

          // Mark as failed
          if (!orderOperations.firestore.attempted) {
            orderOperations.firestore.attempted = true;
          }
          orderOperations.firestore.error = error.message;
          results.failed.push({ order, error: error.message, operations: orderOperations });

          setCompletedOrders((prev) => [
            ...prev,
            {
              order,
              status: "error",
              error: error.message,
              operations: orderOperations,
            },
          ]);
        }
      }

      // Update final progress
      setProgress({ current: orders.length, total: orders.length });
      setCurrentOrder(null);
      setCurrentOperation(null);
      setIsProcessingComplete(true);

      // Set processing results for summary
      setProcessingResults({
        successCount: results.successful.length,
        partialCount: results.partial.length,
        errorCount: results.failed.length,
        total: results.total,
      });

      // Call onSuccess if any orders were processed successfully
      if (results.successful.length > 0) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error processing orders:", error);
      message.error(t("notifications.error_creating_order") + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProgressModalClose = () => {
    setShowProgressModal(false);
    setProgress({ current: 0, total: 0 });
    setCompletedOrders([]);
    setIsProcessingComplete(false);
    setCurrentOrder(null);
    setCurrentOperation(null);
    setProcessingResults(null);
  };

  const productColumns = [
    {
      title: t("Product"),
      key: "productName",
      render: (_, product) => <Text>{`${product.itemCount}x ${product.productName}`}</Text>,
    },
    {
      title: t("price_per_unit"),
      dataIndex: "productPrice",
      key: "productPrice",
      render: (price) => <Text>{CURRENCY + formatPrice(parseFloat(price))}</Text>,
    },
    {
      title: t("total_price"),
      key: "totalPrice",
      render: (_, product) => <Text>{CURRENCY + formatPrice(product.productPrice * product.itemCount)}</Text>,
    },
  ];

  const renderCollapseHeader = (order, index) => {
    const shippingCost = shippingToggles[index] ? parseFloat(order.shippingCost || 0) : 0;
    const calculatedTotal = order.subTotalAmount + shippingCost + (order.deliveryTimeCharge || 0);

    return (
      <Space>
        <Text strong>
          #{index + 1} {order.farmorderCustomerName},
        </Text>
        <Text>{dayjs(order.farmorderDeliveryDate).format("ddd DD-MM-YYYY")},</Text>
        <Text>{CURRENCY + formatPrice(calculatedTotal)}</Text>
        {order.edifactUnknownLines?.length > 0 && (
          <Tag color="warning">
            {order.edifactUnknownLines.length} {t("unknown_products").toLowerCase()}
          </Tag>
        )}
      </Space>
    );
  };

  const renderCollapseHeaderFailedOrder = (order, index) => {
    return (
      <Space>
        <Text strong>{order.order.customer},</Text>
        <Text>{dayjs(order.farmorderDeliveryDate).format("ddd DD-MM-YYYY")}</Text>
        <Text>{`${order.reasons?.length} ${t("error_messages").toLowerCase()}`}</Text>
      </Space>
    );
  };

  return (
    <>
      <Modal
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              {t("preview_orders_modal_title")}
            </Title>
            <Tag color="processing">
              {orders.length} {t("orders").toLowerCase()}
            </Tag>
          </Space>
        }
        open={visible}
        onCancel={onCancel}
        onOk={processOrders}
        okText={t("save")}
        okButtonProps={{ disabled: orders.some((order) => order.edifactUnknownLines?.length > 0) }}
        cancelText={t("cancel")}
        width={900}
        style={{ top: 20 }}
        confirmLoading={loading}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{t("preview_orders_modal_message")}</Text>
        </div>

        {skippedOrders.length > 0 && (
          <div style={{ marginBottom: "2rem" }}>
            <Typography.Title level={4} type={"danger"}>
              {t("failed")}
            </Typography.Title>
            <Collapse
              ghost
              accordion
              size="small"
              style={{ background: "transparent" }}
              items={skippedOrders.map((order, index) => {
                return {
                  key: index,
                  label: renderCollapseHeaderFailedOrder(order, index),
                  children: (
                    <Card size="small" style={{ marginTop: 8 }}>
                      <Descriptions size="small" column={2}>
                        {order.reasons?.map((reason, index) => (
                          <Descriptions.Item label={index + 1} key={index}>
                            <Text>{reason}</Text>
                          </Descriptions.Item>
                        ))}
                      </Descriptions>
                    </Card>
                  ),
                };
              })}
            />
          </div>
        )}

        {orders.length > 0 ? (
          <div>
            <Typography.Title level={4} type={"success"}>
              {t("succeeded")}
            </Typography.Title>
            <Collapse
              ghost
              accordion
              size="small"
              style={{ background: "transparent" }}
              items={orders.map((order, index) => {
                return {
                  key: index,
                  label: renderCollapseHeader(order, index),
                  children: (
                    <Card size="small" style={{ marginTop: 8 }}>
                      <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
                        <Descriptions.Item label={t("customer_name")}>
                          <Text>{order.farmorderCustomerName}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={t("delivery_date")}>
                          <Text>{dayjs(order.farmorderDeliveryDate).format("dddd DD-MM-YYYY")}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={t("region")}>
                          <Text>{order.farmorderRegion}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={t("shipping_costs")}>
                          <Space>
                            <Text>{CURRENCY + formatPrice(parseFloat(order.shippingCost || 0))}</Text>
                            <Switch
                              size="small"
                              checked={shippingToggles[index]}
                              disabled={
                                order.deliveryLabel === JLF_PICKED_UP || parseFloat(order.shippingCost || 0) === 0
                              }
                              onChange={(checked) => {
                                setShippingToggles((prev) => {
                                  const newToggles = [...prev];
                                  newToggles[index] = checked;
                                  return newToggles;
                                });
                              }}
                            />
                          </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label={t("total_amount")}>
                          <Text>
                            {CURRENCY +
                              formatPrice(
                                order.subTotalAmount +
                                  (shippingToggles[index] ? parseFloat(order.shippingCost || 0) : 0) +
                                  (order.deliveryTimeCharge || 0),
                              )}
                          </Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={t("delivery_label")}>
                          <Text>{order.deliveryLabel}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={t("payment_method")}>
                          <Text>{order.paymentMethod}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={t("order.orderer")}>
                          {!!order.farmorderUserFirstName
                            ? order.farmorderUserFirstName + " " + order.farmorderUserLastName
                            : order.farmorderUserId}
                        </Descriptions.Item>
                        <Descriptions.Item label={t("reference")} span={2}>
                          <Input
                            value={references?.[index]}
                            onChange={(e) => {
                              const val = e.target.value;
                              setReferences((prev) => {
                                const copy = [...prev];
                                copy[index] = val;
                                return copy;
                              });
                            }}
                            placeholder={t("form.enter_reference")}
                            size="small"
                            maxLength={20}
                          />
                        </Descriptions.Item>
                      </Descriptions>
                      <Divider style={{ margin: "12px 0" }} />

                      <Table
                        dataSource={order.userShoppingCart}
                        columns={productColumns}
                        pagination={false}
                        size="small"
                        rowKey={(record, idx) => `${index}-${idx}`}
                        style={{ marginTop: 8 }}
                      />

                      {order.edifactUnknownLines?.length > 0 && (
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginTop: 12 }}
                          message={t("unknown_products_in_order")}
                          description={
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                              {order.edifactUnknownLines.map((line, lineIndex) => (
                                <li key={lineIndex}>
                                  {t("edifact_line")} {line.lineNumber}: {t("barcode")} {line.barcode}
                                  {line.orderedQuantity !== null ? ` — ${line.orderedQuantity}x` : ""}
                                </li>
                              ))}
                            </ul>
                          }
                        />
                      )}

                      {order.edifactWarnings?.map((warning, warningIndex) => (
                        <Alert
                          key={warningIndex}
                          type="warning"
                          showIcon
                          style={{ marginTop: 12 }}
                          message={warning.message}
                        />
                      ))}
                    </Card>
                  ),
                };
              })}
            />
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Text type="secondary">{t("no_succeeded_orders")}</Text>
          </div>
        )}
      </Modal>

      <ProcessOrdersProgressModal
        visible={showProgressModal}
        onClose={handleProgressModalClose}
        progress={progress}
        currentOrder={currentOrder}
        currentOperation={currentOperation}
        completedOrders={completedOrders}
        isComplete={isProcessingComplete}
        summary={processingResults}
      />
    </>
  );
};

export default PreviewExcelOrdersModal;
